import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useSiteContent } from "@/context/SiteContentContext";
import { Save, RotateCcw, Check, AlertCircle, FileText, Search } from "lucide-react";

const PAGE_LABELS = {
  home: "Home page",
  about: "About page",
  grower: "Become a Grower page",
  growers: "Growers index page",
  territories: "Territories index page",
  footer: "Footer",
};

export default function ConsoleSiteContent() {
  const { reload } = useSiteContent();
  const [byPage, setByPage] = useState({});
  const [loading, setLoading] = useState(true);
  const [drafts, setDrafts] = useState({}); // key -> draft string
  const [saving, setSaving] = useState({}); // key -> bool
  const [err, setErr] = useState("");
  const [savedKey, setSavedKey] = useState("");
  const [activePage, setActivePage] = useState("home");
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/site-content");
      setByPage(data.by_page || {});
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const setDraft = (key, value) => setDrafts((d) => ({ ...d, [key]: value }));

  const save = async (entry) => {
    const draft = drafts[entry.key];
    if (draft === undefined || draft === entry.value) return;
    setSaving((s) => ({ ...s, [entry.key]: true }));
    try {
      const { data } = await api.patch(`/admin/site-content/${encodeURIComponent(entry.key)}`, { value: draft });
      // Refresh local state
      setByPage((bp) => {
        const next = { ...bp };
        next[entry.page] = (next[entry.page] || []).map((e) => e.key === entry.key ? data.entry : e);
        return next;
      });
      setDrafts((d) => { const n = { ...d }; delete n[entry.key]; return n; });
      setSavedKey(entry.key);
      setTimeout(() => setSavedKey((k) => k === entry.key ? "" : k), 2000);
      reload(); // refresh the public-facing copy across the app
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving((s) => { const n = { ...s }; delete n[entry.key]; return n; });
    }
  };

  const reset = async (entry) => {
    if (!window.confirm(`Reset "${entry.label}" to its default text?`)) return;
    try {
      const { data } = await api.post(`/admin/site-content/${encodeURIComponent(entry.key)}/reset`);
      setByPage((bp) => {
        const next = { ...bp };
        next[entry.page] = (next[entry.page] || []).map((e) => e.key === entry.key ? data.entry : e);
        return next;
      });
      setDrafts((d) => { const n = { ...d }; delete n[entry.key]; return n; });
      reload();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail));
    }
  };

  const pages = useMemo(() => Object.keys(byPage), [byPage]);
  const currentEntries = useMemo(() => {
    const list = byPage[activePage] || [];
    if (!q.trim()) return list;
    const needle = q.trim().toLowerCase();
    return list.filter((e) =>
      e.key.toLowerCase().includes(needle) ||
      e.label.toLowerCase().includes(needle) ||
      (e.value || "").toLowerCase().includes(needle)
    );
  }, [byPage, activePage, q]);

  return (
    <div data-testid="console-site-content">
      <div className="mb-8">
        <div className="text-[10px] tracking-[0.3em] uppercase text-[var(--bloom-ink-muted)] mb-1">Heritage Bloom Club</div>
        <h1 className="font-serif text-5xl md:text-6xl leading-none">Site Content</h1>
        <p className="font-serif italic text-lg text-[var(--bloom-ink-soft)] mt-3">
          Edit the copy across your public website. Changes go live the moment you save.
        </p>
      </div>

      {/* Page tabs */}
      <div className="flex flex-wrap gap-1 mb-6 border-b border-[var(--bloom-border)] -mx-1" data-testid="site-content-page-tabs">
        {pages.map((p) => {
          const active = activePage === p;
          const count = (byPage[p] || []).length;
          return (
            <button
              key={p}
              onClick={() => setActivePage(p)}
              className={`px-3 py-2.5 mx-1 mb-[-1px] border-b-2 text-sm transition flex items-center gap-2 ${active ? "border-[var(--bloom-ink)] text-[var(--bloom-ink)]" : "border-transparent text-[var(--bloom-ink-muted)] hover:text-[var(--bloom-ink-soft)]"}`}
              data-testid={`site-content-tab-${p}`}
            >
              <FileText size={13} strokeWidth={1.5} />
              <span className="font-serif">{PAGE_LABELS[p] || p}</span>
              <span className="text-[10px] tracking-[0.15em] uppercase text-[var(--bloom-ink-muted)]">{count}</span>
            </button>
          );
        })}
      </div>

      {/* Search */}
      <div className="relative mb-6 max-w-md">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--bloom-ink-muted)]" />
        <input
          type="text"
          placeholder="Find a key or phrase"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="bloom-input pl-9 w-full"
          data-testid="site-content-search"
        />
      </div>

      {err && <div className="mb-4 flex items-center gap-2 text-sm text-[var(--bloom-accent)] font-serif italic"><AlertCircle size={14} /> {err}</div>}

      {loading ? (
        <Loading />
      ) : currentEntries.length === 0 ? (
        <Empty msg="No entries match." />
      ) : (
        <div className="space-y-5">
          {currentEntries.map((entry) => {
            const draft = drafts[entry.key];
            const dirty = draft !== undefined && draft !== entry.value;
            const isSaving = !!saving[entry.key];
            const justSaved = savedKey === entry.key;
            const isDefault = entry.value === entry.default;
            const value = draft !== undefined ? draft : entry.value;

            return (
              <article key={entry.key} className="bloom-card p-5" data-testid={`site-content-row-${entry.key}`}>
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="min-w-0">
                    <div className="font-serif text-base text-[var(--bloom-ink)]">{entry.label}</div>
                    <div className="text-[10px] tracking-[0.2em] uppercase text-[var(--bloom-ink-muted)] mt-1 font-mono">{entry.key}</div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {justSaved && <span className="inline-flex items-center gap-1 text-xs text-[var(--bloom-ink)] font-serif italic"><Check size={12} /> Saved</span>}
                    {!isDefault && (
                      <button
                        onClick={() => reset(entry)}
                        className="bloom-btn bloom-btn-ghost text-xs"
                        title="Reset to default copy"
                        data-testid={`site-content-reset-${entry.key}`}
                      >
                        <RotateCcw size={11} /> Reset
                      </button>
                    )}
                  </div>
                </div>

                {entry.kind === "textarea" ? (
                  <textarea
                    value={value}
                    onChange={(e) => setDraft(entry.key, e.target.value)}
                    rows={Math.max(2, Math.min(6, Math.ceil(value.length / 80)))}
                    className="bloom-input w-full font-serif"
                    data-testid={`site-content-input-${entry.key}`}
                  />
                ) : (
                  <input
                    type="text"
                    value={value}
                    onChange={(e) => setDraft(entry.key, e.target.value)}
                    className="bloom-input w-full font-serif"
                    data-testid={`site-content-input-${entry.key}`}
                  />
                )}

                {dirty && (
                  <div className="mt-3 flex items-center justify-between gap-3">
                    <div className="text-xs font-serif italic text-[var(--bloom-ink-soft)]">
                      Unsaved changes
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setDrafts((d) => { const n = { ...d }; delete n[entry.key]; return n; })}
                        className="bloom-btn bloom-btn-ghost text-sm"
                      >
                        Discard
                      </button>
                      <button
                        onClick={() => save(entry)}
                        disabled={isSaving}
                        className="bloom-btn bloom-btn-primary text-sm"
                        data-testid={`site-content-save-${entry.key}`}
                      >
                        <Save size={12} /> {isSaving ? "Saving…" : "Save"}
                      </button>
                    </div>
                  </div>
                )}
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Loading() { return <div className="bloom-card p-14 text-center font-serif italic text-[var(--bloom-ink-muted)]">Loading…</div>; }
function Empty({ msg }) { return <div className="bloom-card p-14 text-center font-serif italic text-[var(--bloom-ink-muted)]">{msg}</div>; }
